/**
 * App Version Force Update Dialog Component
 *
 * Dialog for forcing updates either immediately or scheduled for a future date.
 * Provides two modes: Force Now and Schedule for Later.
 */

import { useState } from 'react';
import { Clock, Zap, Loader2, Calendar as CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUpdateAppVersion } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';
import type { AppVersion } from '@/types/app-version';

interface AppVersionForceUpdateDialogProps {
  version: AppVersion | null;
  onClose: () => void;
}

/**
 * App Version Force Update Dialog
 * Renders dialog with Force Now and Schedule options
 */
export function AppVersionForceUpdateDialog({
  version,
  onClose,
}: AppVersionForceUpdateDialogProps) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState('12:00');
  const updateAppVersionMutation = useUpdateAppVersion();

  /**
   * Handle Force Now
   * Sets forceUpdateAfter to current timestamp
   */
  const handleForceNow = async () => {
    if (!version) return;

    setIsUpdating(true);
    try {
      await updateAppVersionMutation.mutateAsync({
        id: version.id,
        data: {
          forceUpdateAfter: new Date().toISOString(),
          isMandatory: true, // Force updates are always mandatory
        },
      });

      toast({
        title: 'Force Update Activated',
        description: `Version ${version.version} is now forcing updates immediately`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to force update',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Handle Schedule Update
   * Sets forceUpdateAfter to selected date and time
   */
  const handleScheduleUpdate = async () => {
    if (!version || !selectedDate) return;

    // Combine date and time
    const [hours, minutes] = selectedTime.split(':');
    const scheduledDate = new Date(selectedDate);
    scheduledDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    setIsUpdating(true);
    try {
      await updateAppVersionMutation.mutateAsync({
        id: version.id,
        data: {
          forceUpdateAfter: scheduledDate.toISOString(),
          isMandatory: true, // Force updates are always mandatory
        },
      });

      toast({
        title: 'Force Update Scheduled',
        description: `Version ${version.version} will force updates on ${scheduledDate.toLocaleString()}`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Schedule Failed',
        description: error instanceof Error ? error.message : 'Failed to schedule update',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog
      open={version !== null}
      onOpenChange={() => {
        if (!isUpdating) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Force Update
          </DialogTitle>
          <DialogDescription>
            Force users to update to version {version?.version} either immediately or at a scheduled
            time.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="now" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="now">Force Now</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          </TabsList>

          {/* Force Now Tab */}
          <TabsContent value="now" className="space-y-4">
            <div className="bg-muted p-4 rounded-md space-y-2">
              <p className="text-sm font-medium">Immediate Force Update</p>
              <p className="text-sm text-muted-foreground">
                This will immediately force all users to update to version {version?.version}. Users
                will not be able to dismiss the update dialog.
              </p>
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-md">
              <Zap className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This action will take effect immediately and cannot be undone
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                Cancel
              </Button>
              <Button onClick={handleForceNow} disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Force Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={isUpdating}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? selectedDate.toLocaleDateString() : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={date => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Select Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={selectedTime}
                  onChange={e => setSelectedTime(e.target.value)}
                  disabled={isUpdating}
                />
              </div>
            </div>

            <div className="bg-muted p-4 rounded-md">
              <p className="text-sm text-muted-foreground">
                The force update will be activated at the selected date and time. Users will be
                forced to update when they next open the app after this time.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                Cancel
              </Button>
              <Button onClick={handleScheduleUpdate} disabled={isUpdating || !selectedDate}>
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Schedule Update
                  </>
                )}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
